import { Mail, Phone, Linkedin, FileText, Eye, Download, QrCode, Smartphone } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { downloadCandidateCv } from "@/components/cv/CvDialog";
import { FavoriteButton, CandidateNotes } from "@/components/portal/CandidateFollowup";
import { ColleagueFeedback } from "@/components/portal/ColleagueFeedback";
import { useAuth } from "@/lib/auth";
import { ORIGIN_LABELS } from "@/lib/i18n/dictInteracciones";
import { fullName, initials, DAY_LONG } from "@/lib/demo/utils";

const ORIGIN_DESC = { company_scanned_candidate: "Currículum compartido presencialmente", candidate_scanned_company: "El candidato escaneó el QR de tu stand", post_event_online: "Currículum enviado online tras el Foro" };

import { useI18n } from "@/lib/i18n/i18n";

const Row = ({ icon: Icon, label, value, testId }) => {
  const { t } = useI18n();
  return (
  <div className="flex items-center gap-4 py-3">
    <Icon className="h-4 w-4 text-slate-400" />
    <div className="min-w-0">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{t(label)}</p>
      <p className="truncate text-sm font-semibold text-slate-900" data-testid={testId}>{value || "—"}</p>
    </div>
  </div>
  );
};

export const CandidateSheet = ({ row, onClose, onViewCv, onChanged }) => {
  const { t } = useI18n();
  const { company } = useAuth();
  return (
  <Sheet open={!!row} onOpenChange={(o) => !o && onClose()}>
    <SheetContent side="right" className="w-full overflow-y-auto p-0 sm:max-w-lg [&>button]:text-white [&>button]:opacity-80" data-testid="candidate-sheet">
      {row && (() => {
        const { participant: p, interaction: i } = row;

        return (
          <div>
            <div className="bg-slate-950 px-8 pb-8 pt-10 text-white">
              <div className="flex items-center justify-between pr-8">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-upv font-display text-xl font-bold">{initials(p)}</span>
                <FavoriteButton pid={p.id} value={i.favorite} onChange={(v) => onChanged({ favorite: v })} className="h-11 gap-2 rounded-full bg-white/10 px-4 text-xs font-bold hover:bg-white/20" testId="candidate-sheet-favorite" />
              </div>
              <SheetHeader className="mt-5 space-y-1 text-left">
                <SheetTitle className="font-display text-2xl font-extrabold uppercase tracking-tight text-white" data-testid="candidate-sheet-name">{fullName(p)}</SheetTitle>
                <SheetDescription className="text-white/70">{t(p.titulacion)}</SheetDescription>
              </SheetHeader>
              <p className="mt-1 text-sm font-bold tracking-wider text-rose-300">{p.centro} · {t(p.curso)}</p>
            </div>
            <div className="space-y-8 px-8 py-8">
              <div className="divide-y divide-slate-100">
                <Row icon={Mail} label="Email" value={p.email} testId="candidate-sheet-email" />
                <Row icon={Phone} label="Teléfono" value={p.phone} testId="candidate-sheet-phone" />
                <Row icon={Linkedin} label="LinkedIn" value={p.linkedin} testId="candidate-sheet-linkedin" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{t("Currículum")}</p>
                <div className="mt-3 flex items-center gap-3 rounded-2xl border border-slate-200 p-4">
                  <FileText className="h-5 w-5 text-upv" /><span className="truncate font-mono text-sm font-semibold">{p.cvFileName}</span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <button onClick={() => onViewCv(p)} className="flex h-12 items-center justify-center gap-2 rounded-xl bg-upv text-sm font-bold tracking-wide text-white transition-colors hover:bg-upv-dark" data-testid="candidate-sheet-view-cv-button"><Eye className="h-4 w-4" /> {t("VISUALIZAR CV")}</button>
                  <button onClick={() => downloadCandidateCv(p, t)} className="flex h-12 items-center justify-center gap-2 rounded-xl border-2 border-slate-900 text-sm font-bold tracking-wide transition-colors hover:bg-slate-900 hover:text-white" data-testid="candidate-sheet-download-cv-button"><Download className="h-4 w-4" /> {t("DESCARGAR CV")}</button>
                </div>
              </div>
              <div className="rounded-2xl bg-slate-50 p-6" data-testid="candidate-sheet-interaction">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-upv">{t("Interacción")}</p>
                <p className="mt-3 font-display text-lg font-bold">{t(ORIGIN_DESC[i.origin] || "Currículum compartido presencialmente")}</p>
                <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
                  <div><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{t("Empresa")}</p><p className="font-semibold">{company?.name}</p></div>
                  <div><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{t("Fecha")}</p><p className="font-semibold" data-testid="candidate-sheet-date">{DAY_LONG[i.day] ? t(DAY_LONG[i.day]) : i.date}</p></div>
                  <div><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{t("Hora")}</p><p className="font-mono font-bold" data-testid="candidate-sheet-time">{i.time}</p></div>
                </div>
                <div className="mt-5 flex flex-wrap gap-2 text-xs font-semibold">
                  <span className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5"><QrCode className="h-3.5 w-3.5 text-upv" /> {t(ORIGIN_LABELS[i.origin] || i.origin)}</span>
                  {i.origin === "company_scanned_candidate" && i.deviceId && <span className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5"><Smartphone className="h-3.5 w-3.5 text-upv" /> Recruiter Device {String(i.deviceId || "").slice(3)}</span>}
                </div>
              </div>
              <ColleagueFeedback pid={p.id} />
              <CandidateNotes pid={p.id} onCount={(n) => onChanged({ notesCount: n })} />
            </div>
          </div>
        );
      })()}
    </SheetContent>
  </Sheet>
  );
};
